import axios from "axios";
import "firebase/auth";
import { getAuth } from "firebase/auth";
import { saveAs } from "file-saver";

export const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BASE_URL,
});

export const userLogin = async () => {
  const token = await getAuth().currentUser?.getIdToken();
  const response = await api.post(
    "/users/login",
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

export const getUserOrgs = async () => {
  const token = await getAuth().currentUser?.getIdToken();
  const response = await api.get("/users/orgs", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const getUserInformation = async (
  userId: string,
  organizationId: string | null
) => {
  const token = await getAuth().currentUser?.getIdToken();
  const response = await api.get(`/users/${userId}/${organizationId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const deleteUserAccount = async () => {
  const token = await getAuth().currentUser?.getIdToken();
  const response = await api.delete("/users/delete-user", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const updateUserInformation = async (
  userId: string,
  organizationId: string | null,
  role: string
) => {
  const token = await getAuth().currentUser?.getIdToken();

  const response = await api.put(
    `/users/${userId}/${organizationId}?role=${role}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

export const associateUsersToOrganization = async (
  organizationId: string | null,
  userEmails: string[],
  role: string
) => {
  const token = await getAuth().currentUser?.getIdToken();

  const params = new URLSearchParams();
  userEmails.forEach((email) => params.append("user_emails", email));
  params.append("role", role);

  const url = `/users/${organizationId}?${params.toString()}`;

  const response = await api.post(
    url,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response.data;
};

export const removeUserFromOrganization = async (
  organizationId: string | null,
  userId: string
) => {
  const token = await getAuth().currentUser?.getIdToken();
  const response = await api.delete(`/users/${userId}/${organizationId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

const formatDate = (date: Date) => {
  return date.toISOString().slice(0, 19).replace("T", "_").replace(/:/g, "-");
};

export const getUsersDetails = async (
  organizationId: string | null,
  startDate: string | null,
  endDate: string | null
) => {
  if (!organizationId) {
    throw new Error("Organization ID is required");
  }

  const token = await getAuth().currentUser?.getIdToken();
  let queryString = `organization_id=${organizationId}`;
  if (startDate) {
    queryString += `&start_timestamp=${startDate}`;
  }
  if (endDate) {
    queryString += `&end_timestamp=${endDate}`;
  }

  const response = await api.get(`/users/users-details?${queryString}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    responseType: "blob",
  });

  const dateTime = formatDate(new Date());
  const organizationName = response.headers["x-organization-name"];

  const filename = `users_details_${
    organizationName ? organizationName : organizationId
  }_${dateTime}.csv`;

  const blob = new Blob([response.data], { type: "text/csv" });
  saveAs(blob, filename);

  return response.data;
};
